import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { Owner } from '../bookings/types';

// Pure: build the GET /api/owners?mode=picker typeahead query for a search term.
// A trimmed, non-empty term is sent as `q`; empty/whitespace is omitted so the
// server returns recents capped at 10 when there's no query; an active search
// lifts the cap (pickerLimit) so a real lookup isn't truncated to the 10 newest.
// No offset/filter — the picker is recents-or-search, no chip (api.md mode=picker).
export function buildOwnerPickerQuery(q: string | null): string {
  const p = new URLSearchParams();
  p.set('mode', 'picker');
  const term = q?.trim();
  if (term) p.set('q', term);
  return `/owners?${p.toString()}`;
}

// Resolves owners for the booking-modal pickers by typeahead against the server
// (mode=picker), replacing the previous load-every-owner-on-open. Each result
// carries its dogs embedded (ADR-3) so selecting an owner renders the dog grid
// with no second call. Fetches when enabled; refetches when `q` changes (a short
// debounce keeps it off every keystroke) and on `reload()` after an owner edit.
// A request-gen guard discards responses from a superseded query.
export function useOwners(
  enabled: boolean,
  q: string | null = null,
): { owners: Owner[]; loading: boolean; reload: () => void } {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const genRef = useRef(0);
  // A nonce bump is a reload() after an owner edit (W4 stales AP5); the picker
  // re-reads with the browser cache bypassed so the edited owner shows fresh
  // (read-cache-strategy AC3/AC4). A q change re-reads cacheable.
  const prevNonceRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setOwners([]);
      return;
    }
    const gen = ++genRef.current;
    const isReload = nonce !== prevNonceRef.current;
    prevNonceRef.current = nonce;
    setLoading(true);
    // Debounce the typeahead so a fast typist doesn't fire a request per key;
    // the first (empty-q) load still feels immediate enough at 150ms.
    const handle = setTimeout(() => {
      api<Owner[]>(buildOwnerPickerQuery(q), isReload ? { cache: 'reload' } : {})
        .then((body) => {
          if (gen !== genRef.current) return; // superseded by a newer query
          setOwners(body.data ?? []);
        })
        .catch(() => {
          /* 401 already redirected inside the client; otherwise the picker just
             stays empty and the modal still works for a known booking */
        })
        .finally(() => {
          if (gen === genRef.current) setLoading(false);
        });
    }, 150);
    return () => clearTimeout(handle);
  }, [enabled, q, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { owners, loading, reload };
}
